import { useState } from "react";
import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import {
  useGetTrainerClients,
  useSendClientSms,
  useGetTrainerMessageTemplates,
  useCreateTrainerMessageTemplate,
  useUpdateTrainerMessageTemplate,
  useDeleteTrainerMessageTemplate,
  getGetTrainerClientsQueryKey,
  getGetTrainerMessageTemplatesQueryKey,
} from "@workspace/api-client-react";
import type { TrainerClient, TrainerMessageTemplate, SmsResult } from "@workspace/api-client-react";
import { format, differenceInDays } from "date-fns";
import {
  Users, MessageSquare, Phone, PhoneOff, Send, Plus,
  Pencil, Trash2, Loader2, Search, CheckCircle, XCircle,
  FileText, Package, Clock, AlertTriangle, ChevronDown, ChevronUp,
} from "lucide-react";

interface StudentCreditPackage {
  purchaseId: string;
  packageName: string;
  creditsRemaining: number;
  creditsTotal: number;
  expiresAt: string | null;
  purchasedAt: string;
}

interface StudentCredit {
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string | null;
  totalCreditsRemaining: number;
  packages: StudentCreditPackage[];
}

export default function TrainerClients() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<TrainerClient | null>(null);
  const [smsMessage, setSmsMessage] = useState("");
  const [smsResult, setSmsResult] = useState<SmsResult | null>(null);

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TrainerMessageTemplate | null>(null);
  const [templateTitle, setTemplateTitle] = useState("");
  const [templateBody, setTemplateBody] = useState("");
  const [templateContext, setTemplateContext] = useState("GENERAL");

  const [expandedStudents, setExpandedStudents] = useState<Set<string>>(new Set());

  const { data: clients, isLoading: clientsLoading } = useGetTrainerClients();
  const { data: templates } = useGetTrainerMessageTemplates();
  const { data: studentCredits, isLoading: creditsLoading } = useQuery({
    queryKey: ["trainer", "student-credits"],
    queryFn: async () => {
      const res = await fetch("/api/trainers/my/student-credits", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch student credits");
      return res.json() as Promise<StudentCredit[]>;
    },
  });

  function toggleExpand(studentId: string) {
    setExpandedStudents(prev => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId); else next.add(studentId);
      return next;
    });
  }

  function getDaysUntilExpiry(expiresAt: string | null): number | null {
    if (!expiresAt) return null;
    return differenceInDays(new Date(expiresAt), new Date());
  }

  function expiryColor(days: number | null): string {
    if (days === null) return "text-muted-foreground";
    if (days <= 3) return "text-red-400";
    if (days <= 7) return "text-orange-400";
    if (days <= 14) return "text-yellow-400";
    return "text-green-400";
  }

  const sendSmsMutation = useSendClientSms({
    mutation: {
      onSuccess: (data) => {
        setSmsResult(data);
        if (data.sent) {
          toast({ title: "SMS sent", description: "Your message was delivered successfully." });
        } else {
          toast({
            title: "SMS failed",
            description: data.friendlyMessage || "Failed to send SMS. Please try again.",
            variant: "destructive",
          });
        }
      },
      onError: () => {
        toast({ title: "Error", description: "Failed to send SMS.", variant: "destructive" });
      },
    },
  });

  const createTemplateMutation = useCreateTrainerMessageTemplate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetTrainerMessageTemplatesQueryKey() });
        setTemplateDialogOpen(false);
        resetTemplateForm();
        toast({ title: "Template created", description: "Your message template has been saved." });
      },
      onError: () => {
        toast({ title: "Error", description: "Failed to create template.", variant: "destructive" });
      },
    },
  });

  const updateTemplateMutation = useUpdateTrainerMessageTemplate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetTrainerMessageTemplatesQueryKey() });
        setTemplateDialogOpen(false);
        resetTemplateForm();
        toast({ title: "Template updated", description: "Your message template has been updated." });
      },
      onError: () => {
        toast({ title: "Error", description: "Failed to update template.", variant: "destructive" });
      },
    },
  });

  const deleteTemplateMutation = useDeleteTrainerMessageTemplate({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetTrainerMessageTemplatesQueryKey() });
        toast({ title: "Template deleted", description: "Your message template has been removed." });
      },
      onError: () => {
        toast({ title: "Error", description: "Failed to delete template.", variant: "destructive" });
      },
    },
  });

  function resetTemplateForm() {
    setEditingTemplate(null);
    setTemplateTitle("");
    setTemplateBody("");
    setTemplateContext("GENERAL");
  }

  function openSmsDialog(client: TrainerClient) {
    setSelectedClient(client);
    setSmsMessage("");
    setSmsResult(null);
    setSmsDialogOpen(true);
  }

  function handleSendSms() {
    if (!selectedClient || !smsMessage.trim()) return;
    sendSmsMutation.mutate({
      clientId: selectedClient.id,
      data: { message: smsMessage.trim() },
    });
  }

  function openCreateTemplate() {
    resetTemplateForm();
    setTemplateDialogOpen(true);
  }

  function openEditTemplate(template: TrainerMessageTemplate) {
    setEditingTemplate(template);
    setTemplateTitle(template.title);
    setTemplateBody(template.body);
    setTemplateContext(template.context);
    setTemplateDialogOpen(true);
  }

  function handleSaveTemplate() {
    if (!templateTitle.trim() || !templateBody.trim()) return;
    if (editingTemplate) {
      updateTemplateMutation.mutate({
        id: editingTemplate.id,
        data: { title: templateTitle.trim(), body: templateBody.trim(), context: templateContext },
      });
    } else {
      createTemplateMutation.mutate({
        data: { title: templateTitle.trim(), body: templateBody.trim(), context: templateContext },
      });
    }
  }

  function applyTemplate(template: TrainerMessageTemplate) {
    setSmsMessage(template.body);
  }

  const filteredClients = (clients || []).filter((c) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      c.firstName.toLowerCase().includes(q) ||
      c.lastName.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q)
    );
  });

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-foreground mb-2">My Clients</h1>
          <p className="text-muted-foreground text-lg">
            Manage your clients and send them SMS messages
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="glass-panel border-border">
            <CardHeader className="pb-2">
              <p className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Total Clients</p>
              <CardTitle className="text-4xl font-mono text-primary">{clients?.length || 0}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-border">
            <CardHeader className="pb-2">
              <p className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">SMS Ready</p>
              <CardTitle className="text-4xl font-mono text-green-400">
                {clients?.filter((c) => c.hasPhone && c.smsEnabled).length || 0}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card className="glass-panel border-border">
            <CardHeader className="pb-2">
              <p className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Templates</p>
              <CardTitle className="text-4xl font-mono text-foreground">{templates?.length || 0}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        <Tabs defaultValue="clients" className="w-full">
          <TabsList className="bg-white/5 border border-white/10 h-10 mb-4">
            <TabsTrigger value="clients" className="data-[state=active]:bg-primary data-[state=active]:text-black font-semibold px-5">
              Clients
              {clients && clients.length > 0 && (
                <span className="ml-2 bg-white/10 text-xs rounded-full px-2 py-0.5">{clients.length}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="credits" className="data-[state=active]:bg-primary data-[state=active]:text-black font-semibold px-5">
              Credits
              {studentCredits && studentCredits.length > 0 && (
                <span className="ml-2 bg-white/10 text-xs rounded-full px-2 py-0.5">{studentCredits.length}</span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── CLIENTS TAB ── */}
          <TabsContent value="clients" className="mt-0">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-display font-bold text-foreground">Client List</h2>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search clients..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {clientsLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : filteredClients.length === 0 ? (
                  <div className="p-8 text-center rounded-2xl border border-dashed border-border bg-muted/30">
                    <Users className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                    <p className="text-foreground font-medium">
                      {searchQuery ? "No clients match your search" : "No clients yet"}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {searchQuery
                        ? "Try a different search term"
                        : "Clients are added automatically when they book a session with you."}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredClients.map((client) => (
                      <div
                        key={client.id}
                        className="p-4 rounded-xl bg-card border border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-lg font-bold text-primary shrink-0">
                            {client.firstName[0]}{client.lastName[0]}
                          </div>
                          <div>
                            <h4 className="font-bold text-foreground text-lg">
                              {client.firstName} {client.lastName}
                            </h4>
                            <p className="text-sm text-muted-foreground">{client.email}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span>{client.totalSessions} session{client.totalSessions !== 1 ? "s" : ""}</span>
                              {client.lastSessionDate && (
                                <span>Last: {format(new Date(client.lastSessionDate), "MMM d, yyyy")}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!client.hasPhone ? (
                            <Badge variant="outline" className="text-amber-500 border-amber-500/30">
                              <PhoneOff className="w-3 h-3 mr-1" />
                              No phone
                            </Badge>
                          ) : !client.smsEnabled ? (
                            <Badge variant="outline" className="text-amber-500 border-amber-500/30">
                              <PhoneOff className="w-3 h-3 mr-1" />
                              SMS opted out
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-green-500 border-green-500/30">
                              <Phone className="w-3 h-3 mr-1" />
                              SMS ready
                            </Badge>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!client.hasPhone || !client.smsEnabled}
                            onClick={() => openSmsDialog(client)}
                            className="border-primary/30 text-primary hover:bg-primary/10"
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Text
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="w-full lg:w-80 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-display font-bold text-foreground">Templates</h2>
                  <Button size="sm" variant="outline" onClick={openCreateTemplate}>
                    <Plus className="w-4 h-4 mr-1" />
                    New
                  </Button>
                </div>

                {!templates || templates.length === 0 ? (
                  <div className="p-6 text-center rounded-xl border border-dashed border-border bg-muted/30">
                    <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                    <p className="text-sm text-muted-foreground">No templates yet</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Create reusable message templates for quick sends
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {templates.map((template) => (
                      <Card key={template.id} className="bg-card border-border">
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-foreground text-sm truncate">{template.title}</p>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{template.body}</p>
                              <Badge variant="secondary" className="mt-1.5 text-[10px]">
                                {template.context}
                              </Badge>
                            </div>
                            <div className="flex gap-1 shrink-0">
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7"
                                onClick={() => openEditTemplate(template)}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => deleteTemplateMutation.mutate({ id: template.id })}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ── CREDITS TAB ── */}
          <TabsContent value="credits" className="mt-0 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-display font-bold text-foreground">Client Credits</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Active package credits across your clients — only shows non-expired, non-empty packages
                </p>
              </div>
            </div>

            {creditsLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : !studentCredits || studentCredits.length === 0 ? (
              <div className="p-10 text-center rounded-2xl border border-dashed border-border bg-muted/30">
                <Package className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                <p className="text-foreground font-medium">No active credits</p>
                <p className="text-sm text-muted-foreground mt-1">
                  None of your clients currently have available package credits.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {studentCredits.map((student) => {
                  const isExpanded = expandedStudents.has(student.studentId);
                  const nearestExpiry = student.packages
                    .map(p => getDaysUntilExpiry(p.expiresAt))
                    .filter((d): d is number => d !== null)
                    .sort((a, b) => a - b)[0] ?? null;
                  const urgentCount = student.packages.filter(p => {
                    const d = getDaysUntilExpiry(p.expiresAt);
                    return d !== null && d <= 7;
                  }).length;

                  return (
                    <div key={student.studentId} className="rounded-xl bg-card border border-border overflow-hidden">
                      {/* Student row */}
                      <button
                        onClick={() => toggleExpand(student.studentId)}
                        className="w-full p-4 flex items-center justify-between gap-4 hover:bg-white/5 transition-colors text-left"
                      >
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="w-11 h-11 rounded-full bg-primary/20 flex items-center justify-center text-base font-bold text-primary shrink-0">
                            {student.firstName[0]}{student.lastName[0]}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-bold text-foreground">
                                {student.firstName} {student.lastName}
                              </h4>
                              {urgentCount > 0 && (
                                <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-[10px]">
                                  <AlertTriangle className="w-2.5 h-2.5 mr-1" />
                                  {urgentCount} expiring soon
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground truncate">{student.email}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-4 shrink-0">
                          {/* Total credits pill */}
                          <div className="text-right">
                            <p className="text-2xl font-mono font-bold text-primary leading-none">
                              {student.totalCreditsRemaining}
                            </p>
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">credits</p>
                          </div>
                          {/* Nearest expiry */}
                          {nearestExpiry !== null && (
                            <div className={`text-right hidden sm:block ${expiryColor(nearestExpiry)}`}>
                              <div className="flex items-center gap-1 justify-end">
                                <Clock className="w-3 h-3" />
                                <span className="text-xs font-medium">
                                  {nearestExpiry <= 0 ? "Expired" : `${nearestExpiry}d`}
                                </span>
                              </div>
                              <p className="text-[10px] text-muted-foreground">nearest expiry</p>
                            </div>
                          )}
                          {isExpanded
                            ? <ChevronUp className="w-4 h-4 text-muted-foreground" />
                            : <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          }
                        </div>
                      </button>

                      {/* Expanded package rows */}
                      {isExpanded && (
                        <div className="border-t border-border divide-y divide-border">
                          {student.packages.map((pkg) => {
                            const days = getDaysUntilExpiry(pkg.expiresAt);
                            const usedPct = Math.round(((pkg.creditsTotal - pkg.creditsRemaining) / pkg.creditsTotal) * 100);
                            return (
                              <div key={pkg.purchaseId} className="px-4 py-3 flex items-center gap-4">
                                <Package className="w-4 h-4 text-muted-foreground shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-foreground truncate">{pkg.packageName}</p>
                                  {/* Credits bar */}
                                  <div className="flex items-center gap-2 mt-1.5">
                                    <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-primary rounded-full transition-all"
                                        style={{ width: `${100 - usedPct}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-muted-foreground shrink-0">
                                      {pkg.creditsRemaining}/{pkg.creditsTotal}
                                    </span>
                                  </div>
                                </div>
                                {/* Expiry */}
                                <div className={`text-right shrink-0 ${expiryColor(days)}`}>
                                  {days === null ? (
                                    <span className="text-xs text-muted-foreground">No expiry</span>
                                  ) : (
                                    <>
                                      <p className="text-xs font-semibold">
                                        {days <= 0 ? "Expired" : `${days}d left`}
                                      </p>
                                      <p className="text-[10px] text-muted-foreground">
                                        {format(new Date(pkg.expiresAt!), "MMM d")}
                                      </p>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={smsDialogOpen} onOpenChange={setSmsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              Send SMS to {selectedClient?.firstName} {selectedClient?.lastName}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {templates && templates.length > 0 && (
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Use a template
                </label>
                <Select onValueChange={(val) => {
                  const tmpl = templates.find((t) => t.id === val);
                  if (tmpl) applyTemplate(tmpl);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">
                Message
              </label>
              <Textarea
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                placeholder="Type your message..."
                rows={4}
                maxLength={1000}
              />
              <p className="text-xs text-muted-foreground mt-1">{smsMessage.length}/1000 characters</p>
            </div>

            {smsResult && (
              <div
                className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
                  smsResult.sent
                    ? "bg-green-500/10 text-green-500 border border-green-500/20"
                    : "bg-red-500/10 text-red-500 border border-red-500/20"
                }`}
              >
                {smsResult.sent ? (
                  <CheckCircle className="w-4 h-4 shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 shrink-0" />
                )}
                <span>
                  {smsResult.sent
                    ? "Message sent successfully!"
                    : smsResult.friendlyMessage || "Failed to send message."}
                </span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSmsDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={handleSendSms}
              disabled={!smsMessage.trim() || sendSmsMutation.isPending}
            >
              {sendSmsMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send SMS
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? "Edit Template" : "Create Template"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Title</label>
              <Input
                value={templateTitle}
                onChange={(e) => setTemplateTitle(e.target.value)}
                placeholder="e.g., Check-in, Special Deal"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Category</label>
              <Select value={templateContext} onValueChange={setTemplateContext}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GENERAL">General</SelectItem>
                  <SelectItem value="CANCEL">Cancellation</SelectItem>
                  <SelectItem value="AMEND">Reschedule</SelectItem>
                  <SelectItem value="REJECT">Rejection</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Message Body</label>
              <Textarea
                value={templateBody}
                onChange={(e) => setTemplateBody(e.target.value)}
                placeholder="Type your template message..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveTemplate}
              disabled={
                !templateTitle.trim() ||
                !templateBody.trim() ||
                createTemplateMutation.isPending ||
                updateTemplateMutation.isPending
              }
            >
              {(createTemplateMutation.isPending || updateTemplateMutation.isPending) && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              {editingTemplate ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
